import _ from 'lodash';
import Transaction from "models/Transaction.jsx";
import TransactionStorage from "utilities/TransactionStorage.js";
import transactionSerializer from "models/services/transactionSerializer.jsx";
import SearchAdapter from "react/components/search/SearchAdapter.js";
import TransactionFinderFilter from "utilities/transactionFinder/TransactionFinderFilter";
import PlaidTransactionCollection from "utilities/transactionFinder/PlaidTransactionCollection";
import TransactionCollection from "utilities/transactionFinder/TransactionCollection";
import SelectedDates from "utilities/dates/SelectedDates";

export default class TransactionFinder {

	constructor(searchAdapterType = SearchAdapter.TYPE_FUSE) {
		this.updates = 0;
		this.clear();
		globalThis.transactionFinder = this;

		this.searchAdapterType = searchAdapterType;
		this.refreshSearchList();


		this.loadOfflineTransactions();
	}

	/**
	 *
	 * @returns {TransactionFinderFilter}
	 */
	getTransactionFinderFilter() {
		return new TransactionFinderFilter(this);
	}

	/**
	 * Promise returns an array
	 * @param name
	 * @returns {*|Promise}
	 */
	async searchByQuery(name) {
		return this.searchList.search(name);
	}

	/**
	 * What the transactions page's search looks through: the budget's transactions that are not
	 * deleted, or with `bankTransactions`, what the "Unbudgeted bank activity" tab lists in any month —
	 * the bank records no transaction is linked to, and the hidden ones only with `showHidden`.
	 */
	getSearchableTransactions(bankTransactions = false, showHidden = false) {
		const transactions = this.getAllTransactions();
		if(!bankTransactions) {
			return transactions.filter((transaction) => !transaction.deleted);
		}
		const linked = new Set();
		for(const transaction of transactions) {
			if(transaction.plaid_transaction_id) {
				linked.add(transaction.plaid_transaction_id);
			}
		}
		const { transactionsThatHavePendingParent } = this.plaidTransactions;
		return this.plaidTransactions.toArray().filter((bankTransaction) => {
			return !linked.has(bankTransaction.plaid_transaction_id)
				&& !linked.has(bankTransaction.plaid_pending_transaction_id)
				&& !transactionsThatHavePendingParent[bankTransaction.plaid_transaction_id]
				&& (showHidden || !bankTransaction.hidden);
		});
	}

	refreshSearchList() {
		// console.log('loading up a searcher', this.searchAdapterType);

		if(!this.searchList) {
			const options = {
				includeScore: true,
				findAllMatches: true,
				// equivalent to `keys: [['author', 'tags', 'value']]`
				keys: [['name']]
			};
			this.searchList = SearchAdapter.create(this.searchAdapterType, [], options);

			this.searchList.setListRequestedCallback(() => {
				const list = [];
				this.transactionList.transactions.forEach((transaction) => {
					list.push({
						name: transaction.where,
						amount: transaction.amount,
						transaction: transaction
					});
				});
				return list;
			});
		}
		this.searchList.listUpdated();
	}

	clear() {
		this.plaidTransactions = new PlaidTransactionCollection();
		this.clearSiteTransactions();
	}

	clearSiteTransactions() {
		this.transactionList = new TransactionCollection();
		this.transactionsById = {};
		this.transactionsBySubmitIds = {};

		this.categoryTransactions = {};
	}

	hasTransactions() {
		return true;
	}

	addTransaction(transaction, debug = false) {
		if(!transaction) {
			throw new Error('Transaction is required!');
		}
		this.transactionList.addTransaction(transaction);

		if(transaction.id && transaction.id > 0) {
			this.transactionsById[transaction.id] = transaction;
		}
		if(transaction.submit_id) {
			this.transactionsBySubmitIds[transaction.submit_id] = transaction;
			TransactionStorage.setItem(transaction.submit_id, transactionSerializer(transaction))
				.catch((error) => {
					console.error(error);
				});
		}

		if(transaction.isSplit()) {
			transaction.splits.forEach((split) => {
				this.addCategoryTransaction(split.category_id, transaction);
			});
		} else {
			this.addCategoryTransaction(transaction.category_id, transaction);
		}

		this.refreshSearchList();
		this.updates++;
	}

	getCategoryTransactions(categoryId) {
		if(!categoryId) {
			categoryId = null;
		}
		if(!this.categoryTransactions[categoryId]) {
			this.categoryTransactions[categoryId] = new TransactionCollection();
		}
		return this.categoryTransactions[categoryId];
	}

	getUncategorizedTransactions() {
		return this.getCategoryTransactions(undefined);
	}

	addCategoryTransaction(categoryId, transaction) {
		this.getCategoryTransactions(categoryId).addTransaction(transaction);
	}

	removeCategoryTransaction(categoryId, transaction) {
		this.getCategoryTransactions(categoryId).removeTransaction(transaction);
	}

	hasUpdatePending(transaction) {
		if(this.transactionsBySubmitIds[transaction.submit_id]) {
			return true;
		}
		return false;
	}

	getOfflineTransactions() {
		const transactions = this.getAllTransactions();

		const offlineTransactions = transactions.filter((transaction) => {
			return !!transaction.submit_id;
		});

		return Promise.resolve(offlineTransactions);
	}

	loadOfflineTransactions() {
		// console.log('no storage?', TransactionStorage);
		return TransactionStorage.iterate((transaction, key) => {
				return this.addTransaction(transaction, true);
			})
			.catch((error) => {
				console.error(error);
			});
	}

	setBankTransactions(bankTransactions) {
		for(let bankTransaction of bankTransactions) {
			this.plaidTransactions.addTransaction(bankTransaction);
		}
		this.updates++;
	}

	getTransactionById(id) {
		return this.transactionsById[id] || null;
	}

	getTransactionBySubmitId(submitId) {
		return this.transactionsBySubmitIds[submitId] || null;
	}

	getBankAccountTransactions(date) {
		const allTransactions = [];


		if(_.isPlainObject(date)) {
			_.each(date, (singleDate) => {
				allTransactions.push(...this.getBankAccountTransactions(singleDate));
			});
			return allTransactions;
		}

		return this.plaidTransactions.byMonth(date.year(), date.month());
	}

	setTransactions(transactions) {
		this.clearSiteTransactions();
		transactions.forEach((transaction) => {
			this.addTransaction(transaction);
		});
	}

	getAllTransactions() {
		return this.transactionList.toArray();
	}

	removeTransaction(transaction) {
		const previousTransaction = this.transactionList.retrieveSimilarTransactionByKey(transaction);

		if(!previousTransaction) {
			this.updates++;
		}

		if(previousTransaction) {
			this.transactionList.removeTransaction(transaction);

			if(previousTransaction.isSplit()) {
				console.log('possible bug with removing splits');
				previousTransaction.splits.forEach((split) => {
					this.removeCategoryTransaction(split.category_id, previousTransaction);
				});
			} else {
				this.removeCategoryTransaction(transaction.category_id, previousTransaction);
			}
		}

		if(transaction.id && transaction.id > 0) {
			delete this.transactionsById[transaction.id];
		}
		if(transaction.submit_id) {
			delete this.transactionsBySubmitIds[transaction.submit_id];
			TransactionStorage.removeItem(transaction.submit_id)
				.then((value) => {
					return value;
				})
				.catch((error) => {
					console.error(error);
				});
		}
		this.updates++;
	}


	getTransactionsByDate(dates) {
		if(!dates) {
			throw new Error('date is a required parameter');
		}
		const allTransactions = [];
		SelectedDates.loop(dates, (date) => {
			allTransactions.push(...this.transactionList.byMonth(date.year(), date.month()));
		});
		return allTransactions;
	}

	getSimilarTransaction(searchTransaction) {
		throw new Error('Not used currently unless we get mass add back');
		return this.transactionList[searchTransaction.toIndexString()];
	}
}